import {
  type FC,
  type FocusEventHandler,
  type KeyboardEventHandler,
} from 'react';
import {
  Controller,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { useActivatePromoCodeMutation } from '@/api/queries/shop';
import { getMessageFromQueryError } from '@/common/lib/errors';
import { MainButton } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';

import { PromoNotification } from '../PromoNotification/PromoNotification';

import { promoCodeSchema, type PromoErrors } from './config';

interface PromoCodeFieldsType {
  code: string;
}

const PromoCodeForm: FC = () => {
  const { t } = useTranslation();
  const [activatePromoCodeRequest, { isLoading }] =
    useActivatePromoCodeMutation();
  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    reset,
    formState: { errors, isValid },
  } = useForm<PromoCodeFieldsType>({
    resolver: zodResolver(promoCodeSchema),
    defaultValues: { code: '' },
    mode: 'onChange',
  });

  const onFocus: FocusEventHandler<HTMLInputElement> = (event) => {
    setTimeout(() => {
      event.target.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 300);
  };

  const onSubmit: SubmitHandler<PromoCodeFieldsType> = async (data) => {
    try {
      await activatePromoCodeRequest(data)
        .unwrap()
        .then(() => {
          reset();
        });
    } catch (error) {
      const message = getMessageFromQueryError(error);
      setError('root', { message: message || 'DEFAULT' });
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  const onError: SubmitErrorHandler<PromoCodeFieldsType> = () => {};

  const hasError = !!errors.code || !!errors.root;

  return (
    <form
      className="w-full relative z-[1] flex flex-col gap-4"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Controller
        control={control}
        name="code"
        render={({ field: { name, ...props } }) => (
          <Input
            {...props}
            onChange={(event) => {
              clearErrors('root');
              props.onChange(event);
            }}
            isValid={isValid}
            onFocus={onFocus}
            variant="greyPromo"
            className="w-full text-center placeholder:!text-[16px]"
            hasError={hasError}
            placeholder={t('Enter Promo code')}
            enterKeyHint="send"
            onKeyDown={onKeyDown}
          />
        )}
      />
      <MainButton
        isLoading={isLoading}
        disabled={isLoading}
        inactive={!isValid}
        type="submit"
        text={t('Activate')}
      />
      <PromoNotification
        error={errors.root?.message as PromoErrors | 'DEFAULT' | undefined}
      />
    </form>
  );
};

export { PromoCodeForm };
